
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Button } from "@/components/ui/button";
import { Search, X } from "lucide-react";
import { Country } from "@/types/country";
import { City } from "@/types/city";
import { Airport } from "@/types/airport";

interface TransportationFiltersProps {
  searchTerm: string;
  onSearchChange: (value: string) => void;
  sortBy: string;
  onSortChange: (value: string) => void;
  filterBy: string;
  onFilterChange: (value: string) => void;
  onClearFilters: () => void;
  countryId?: string;
  onCountryChange?: (value: string) => void;
  cityId?: string;
  onCityChange?: (value: string) => void;
  airportId?: string;
  onAirportChange?: (value: string) => void;
  countries?: Country[];
  cities?: City[];
  airports?: Pick<Airport, 'id' | 'name_ar' | 'iata_code'>[];
}

export function TransportationFilters({
  searchTerm,
  onSearchChange,
  sortBy,
  onSortChange,
  filterBy,
  onFilterChange,
  onClearFilters,
  countryId,
  onCountryChange,
  cityId,
  onCityChange,
  airportId,
  onAirportChange,
  countries,
  cities,
  airports,
}: TransportationFiltersProps) {
  return (
    <div className="flex flex-wrap gap-4 p-4 bg-white border rounded-lg shadow-sm">
      <div className="flex-1 min-w-64">
        <div className="relative">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
          <Input
            placeholder="البحث..."
            value={searchTerm}
            onChange={(e) => onSearchChange(e.target.value)}
            className="pl-10"
          />
        </div>
      </div>

      {countries && onCountryChange && typeof countryId !== 'undefined' && (
        <div className="min-w-48">
          <Select value={countryId} onValueChange={onCountryChange}>
            <SelectTrigger>
              <SelectValue placeholder="فلترة حسب الدولة" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">كل الدول</SelectItem>
              {countries.map(country => (
                <SelectItem key={country.id} value={country.id}>{country.name_ar}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      )}

      {cities && onCityChange && typeof cityId !== 'undefined' && (
        <div className="min-w-48">
          <Select value={cityId} onValueChange={onCityChange} disabled={!cities.length}>
            <SelectTrigger>
              <SelectValue placeholder="فلترة حسب المدينة" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">كل المدن</SelectItem>
               {cities.map(city => (
                <SelectItem key={city.id} value={city.id}>{city.name_ar}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      )}
      
      {airports && onAirportChange && typeof airportId !== 'undefined' && (
        <div className="min-w-48">
          <Select value={airportId} onValueChange={onAirportChange} disabled={!airports || !airports.length}>
            <SelectTrigger>
              <SelectValue placeholder="فلترة حسب المطار" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">كل المطارات</SelectItem>
               {airports.map(airport => (
                <SelectItem key={airport.id} value={airport.id}>{airport.name_ar} ({airport.iata_code})</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      )}

      <div className="min-w-48">
        <Select value={sortBy} onValueChange={onSortChange}>
          <SelectTrigger>
            <SelectValue placeholder="ترتيب حسب" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="name_ar">الاسم (أ-ي)</SelectItem>
            <SelectItem value="name_ar_desc">الاسم (ي-أ)</SelectItem>
            <SelectItem value="sort_order">الترتيب</SelectItem>
          </SelectContent>
        </Select>
      </div>

      <div className="min-w-48">
        <Select value={filterBy} onValueChange={onFilterChange}>
          <SelectTrigger>
            <SelectValue placeholder="فلترة حسب" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">جميع العناصر</SelectItem>
            <SelectItem value="has_price">لديه سعر</SelectItem>
            <SelectItem value="no_price">بدون سعر</SelectItem>
          </SelectContent>
        </Select>
      </div>

      <Button
        variant="outline"
        onClick={onClearFilters}
        className="flex items-center gap-2"
      >
        <X className="w-4 h-4" />
        مسح الفلاتر
      </Button>
    </div>
  );
}
